import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress } from '@material-ui/core';

import { WarehouseInventoryV2Api } from 'api/warehouseInventoryV2';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

import { ActionModal } from './ActionModal';
import { ActionsCell } from './ActionsCell';
import { AdjustCasesAndLooseUnitsAction } from './actions/adjustCasesAndLooseUnitsAction';
import { BulkAdjustUnitsPerCaseAction } from './actions/bulkAdjustUnitsPerCaseAction';
import { ChangeExpirationDateAction } from './actions/changeExpirationDateAction';
import { MoveCasesAndLooseUnitsFromKnownOriginAction } from './actions/moveCasesAndLooseUnitsAction';
import { PrintCaseLabelAction } from './actions/printCaseLabelAction';
import { UpdatePartnerLotAction } from './actions/updatePartnerLotAction';

import './locationInventoryV2Styles.css';

const warehouseInventoryV2Api = new WarehouseInventoryV2Api();

function PalletTabExpansion({
    locationId,
    locationName,
    locationType,
    locInvType,
    onLinkClicked,
    onSuccess,
    palletId,
}) {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [rows, setRows] = useState([]);
    const [viewActions, setViewActions] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [actions, setActions] = useState([]);
    const [loadingLots, setLoadingLots] = useState(false);
    const { user } = useContext(UserContext);
    const isMounted = useRef(true);

    useEffect(
        () => () => {
            isMounted.current = false;
        },
        [],
    );

    useEffect(() => {
        getLotDataForPallet();
        setActions(actionDefinitions);
    }, [palletId, selectedRow, locationType]);

    const columns = [
        {
            Header: 'Lot Id',
            accessor: 'lotId',
            Cell: ({ row }) => (
                <Button variant="text" color="primary" onClick={() => handleLotLinkClick(row.original.lotId)}>
                    {row.original.lotId}
                </Button>
            ),
        },
        { Header: 'Partner Lot', accessor: 'partnerLotNumber' },
        { Header: 'Item Name', accessor: 'itemName' },
        {
            Header: 'External SKU',
            accessor: 'externalSku',
            Cell: ({ row }) => (
                <Button
                    variant="text"
                    color="primary"
                    onClick={() => handleSkuLinkClick(row.original.externalSku, row.original.type)}
                >
                    {row.original.externalSku}
                </Button>
            ),
        },
        { Header: 'Partner Sku', accessor: 'partnerSku' },
        { Header: 'Expiration Date', accessor: 'expirationDate' },
        { Header: 'Units per Case', accessor: 'unitsPerCase' },
        { Header: 'Cases', accessor: 'numCases' },
        { Header: 'Loose Units', accessor: 'numLooseUnits' },
        { Header: 'Total Units', accessor: 'totalUnits' },
        { Header: 'PO', accessor: 'purchaseOrderId' },
        { Header: 'Received Date', accessor: 'receivedDate' },
        {
            Header: 'Actions',
            Cell: ({ row }) => (
                <ActionsCell row={row} setSelectedRow={setSelectedRow} setViewActions={setViewActions} />
            ),
        },
    ];

    // API call to GET data
    const getLotDataForPallet = async () => {
        if (isMounted.current && !loadingLots) {
            setLoadingLots(true);
            const searchValues = [{ searchBy: 'palletId', searchTerm: palletId }];
            const result = await warehouseInventoryV2Api.getLots(user.facilityID, searchValues);
            if (result.status) {
                setRows(
                    result.data.lots.map((lot) => ({
                        ...lot,
                        looseUnits: lot.numLooseUnits,
                        cases: lot.numCases,
                        pallets: lot.numPallets,
                        po: lot.purchaseOrderId,
                        active: lot.totalUnits > 0,
                    })),
                );
            } else {
                setNotification({ text: result.msg, level: 'error' });
            }
            setLoadingLots(false);
        }
    };

    const actionDefinitions = [
        {
            text: 'Bulk Adjust Units per Case',
            value: 'bulkAdjustUnitsPerCase',
            componentJSX() {
                return (
                    <BulkAdjustUnitsPerCaseAction
                        lotId={selectedRow?.lotId}
                        unitsPerCase={selectedRow?.unitsPerCase}
                        onSuccess={() => {
                            handleSuccess('Success', false);
                        }}
                        onCancel={handleModalClose}
                    />
                );
            },
        },
        {
            text: 'Adjust Cases and Loose Units',
            value: 'adjustCasesAndLooseUnits',
            componentJSX() {
                return (
                    <AdjustCasesAndLooseUnitsAction
                        palletId={palletId}
                        lotId={selectedRow?.lotId}
                        caseNum={selectedRow?.cases}
                        looseUnitNum={selectedRow?.looseUnits}
                        onSuccess={handleSuccess}
                        onCancel={handleModalClose}
                        locInvType={locInvType}
                        palletNum={selectedRow?.pallets}
                        unitsPerCase={selectedRow?.unitsPerCase}
                    />
                );
            },
        },
        {
            text: 'Move Cases and Loose Units',
            value: 'moveCaseAndLooseUnits',
            componentJSX() {
                return (
                    <MoveCasesAndLooseUnitsFromKnownOriginAction
                        lotId={selectedRow?.lotId}
                        locationName={locationName}
                        locationId={locationId}
                        locationInventoryType={locInvType}
                        onSuccess={handleSuccess}
                        fromPalletId={palletId}
                        cases={selectedRow?.cases}
                        units={selectedRow?.looseUnits}
                        unitsPerCase={selectedRow?.unitsPerCase}
                    />
                );
            },
        },
        {
            text: 'Print Case Label',
            value: 'printCaseLabel',
            componentJSX() {
                return <PrintCaseLabelAction lotId={selectedRow?.lotId} onSuccess={handleSuccess} />;
            },
        },
        {
            text: 'Change Expiration Date',
            value: 'changeExpirationDate',
            componentJSX() {
                return (
                    <ChangeExpirationDateAction
                        lotId={selectedRow?.lotId}
                        onSuccess={handleSuccess}
                        onCancel={handleModalClose}
                        expLifeDays={selectedRow?.expireLifeDays ?? null}
                    />
                );
            },
        },
        {
            text: 'Update Partner Lot Number',
            value: 'updatePartnerLotNumber',
            componentJSX() {
                return (
                    <UpdatePartnerLotAction
                        lotId={selectedRow?.lotId}
                        partnerLotNumber={selectedRow?.partnerLotNumber}
                        onSuccess={handleSuccess}
                    />
                );
            },
        },
    ];

    const handleSkuLinkClick = (externalSku, type) => {
        onLinkClicked({ tabIndex: 2, searchBy: 'externalSku', searchTerm: externalSku, type });
    };

    const handleLotLinkClick = (lotId) => {
        onLinkClicked({ tabIndex: 0, searchBy: 'lotId', searchTerm: lotId });
    };

    const handleModalClose = () => {
        setViewActions(false);
        setSelectedRow({});
    };

    const handleSuccess = (closeModal = true) => {
        onSuccess();
        if (closeModal) {
            handleModalClose();
        }
        getLotDataForPallet();
    };

    return (
        <>
            <Notifications options={notification} />
            <div className="expanded-table-div">
                {loadingLots && <CircularProgress color="inherit" size={48} />}
                {!loadingLots && (
                    <DataTable
                        columns={columns}
                        data={rows}
                        editable={false}
                        tableContainerMaxHeight={600}
                        initialSortBy={[
                            {
                                id: 'expirationDate',
                                desc: false,
                            },
                        ]}
                    />
                )}
                <ActionModal
                    title={`Actions for Lot ${selectedRow.lotId}`}
                    open={viewActions}
                    row={selectedRow}
                    actions={actions}
                    closeModal={handleModalClose}
                />
            </div>
        </>
    );
}

export { PalletTabExpansion };
